import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { useAuth } from "../contexts/AuthContext";

const trustIndicators = [
  "Secure access",
  "Real-time tracking",
  "Safeguarding-focused operations",
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const targetPath = useMemo(() => {
    if (!user) return "/";
    if (user.password_change_required) return "/change-password";
    return `/${user.role}`;
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, user, targetPath, navigate]);

  const validate = () => {
    const nextErrors = {};
    if (!email.trim()) nextErrors.email = "Enter your work email address.";
    if (!password) nextErrors.password = "Enter your password.";
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    const result = await login(email.trim(), password, { rememberMe });
    setSubmitting(false);

    if (result.success) {
      toast.success("Sign-in successful.");
      return;
    }

    toast.error(result.error);
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top_left,rgba(22,61,107,0.08),transparent_32%),linear-gradient(180deg,#F8FAFC_0%,#EEF2F7_100%)]">
      <div className="mx-auto flex min-h-screen max-w-7xl items-center px-6 py-10 lg:px-10">
        <div className="grid w-full overflow-hidden rounded-[36px] border border-white/60 bg-white/90 shadow-[0_30px_120px_rgba(15,23,42,0.12)] backdrop-blur lg:grid-cols-[1.12fr_0.88fr]">
          <section className="relative overflow-hidden bg-[linear-gradient(180deg,#163D6B_0%,#0E2E53_100%)] px-8 py-10 text-white lg:px-12 lg:py-14">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.12),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(183,138,42,0.24),transparent_26%)]" />
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-white/90 backdrop-blur">
                  <ShieldCheck size={16} />
                  CareFleet Guardian platform
                </div>
                <div className="mt-10 max-w-xl space-y-5">
                  <Logo size="large" showText={false} />
                  <h1 className="font-['Manrope'] text-4xl font-semibold tracking-tight text-white lg:text-5xl">
                    Secure access to the CareFleet Guardian platform
                  </h1>
                  <p className="max-w-2xl text-lg leading-8 text-white/82">
                    Manage transport operations, monitor journeys in real time, and support safeguarding across every stage of service delivery.
                  </p>
                </div>
              </div>

              <div className="mt-10 grid gap-3 sm:grid-cols-3">
                {trustIndicators.map((item) => (
                  <div key={item} className="rounded-2xl border border-white/12 bg-white/10 px-4 py-4 backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-sm font-medium text-white">
                      <CheckCircle2 size={16} className="text-[#F3D38B]" />
                      {item}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 text-sm text-white/70">
                © 2026 CareFleet Transport Ltd
              </div>
            </div>
          </section>

          <section className="flex items-center px-6 py-10 lg:px-12 lg:py-14">
            <div className="mx-auto w-full max-w-md">
              <div className="rounded-[32px] border border-[#E2E8F0] bg-white p-8 shadow-[0_16px_60px_rgba(15,23,42,0.06)]">
                <div className="space-y-2">
                  <p className="text-sm font-medium uppercase tracking-[0.22em] text-[#B78A2A]">CareFleet Transport Ltd</p>
                  <h2 className="font-['Manrope'] text-3xl font-semibold tracking-tight text-[#0F172A]">Sign in</h2>
                  <p className="text-sm leading-6 text-[#64748B]">Enter your work email and password to continue.</p>
                </div>

                <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
                  <div>
                    <label htmlFor="email" className="text-sm font-medium text-[#0F172A]">Email</label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="name@carefleettransport.co.uk"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        if (fieldErrors.email) setFieldErrors((current) => ({ ...current, email: undefined }));
                      }}
                      className={`mt-2 w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:ring-4 ${fieldErrors.email ? "border-rose-300 bg-rose-50/60 focus:border-rose-400 focus:ring-rose-100" : "border-[#CBD5E1] bg-white focus:border-[#163D6B] focus:ring-[#163D6B]/10"}`}
                    />
                    {fieldErrors.email && <p className="mt-2 text-sm text-rose-700">{fieldErrors.email}</p>}
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-4">
                      <label htmlFor="password" className="text-sm font-medium text-[#0F172A]">Password</label>
                      <Link to="/forgot-password" className="text-sm font-medium text-[#163D6B] transition hover:text-[#0E2E53]">
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative mt-2">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value);
                          if (fieldErrors.password) setFieldErrors((current) => ({ ...current, password: undefined }));
                        }}
                        className={`w-full rounded-2xl border px-4 py-3 pr-12 text-sm outline-none transition focus:ring-4 ${fieldErrors.password ? "border-rose-300 bg-rose-50/60 focus:border-rose-400 focus:ring-rose-100" : "border-[#CBD5E1] bg-white focus:border-[#163D6B] focus:ring-[#163D6B]/10"}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        className="absolute inset-y-0 right-0 flex items-center px-4 text-[#64748B] transition hover:text-[#0F172A]"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {fieldErrors.password && <p className="mt-2 text-sm text-rose-700">{fieldErrors.password}</p>}
                  </div>

                  <label className="flex items-center gap-3 text-sm text-[#475569]">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(event) => setRememberMe(event.target.checked)}
                      className="h-4 w-4 rounded border-[#CBD5E1] text-[#163D6B]"
                    />
                    Remember me on this device
                  </label>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex h-12 w-full items-center justify-center rounded-2xl bg-[#163D6B] px-4 text-sm font-semibold text-white transition hover:bg-[#0E2E53] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? "Signing in..." : "Sign in securely"}
                  </button>
                </form>

                <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-4 text-sm leading-6 text-[#64748B]">
                  Access is managed by your organisation administrator.
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
